import type ISchoolPeriodUserRepository from '@/core/domain/school/user/ISchoolPeriodUserRepository'
import type { SchoolPeriodUser } from '@/core/domain/school/user/SchoolPeriodUser'
import { SchoolPeriodUserInput } from '@/core/domain/school/user/SchoolPeriodUser'
import type { AvailableEducationalMember } from '@/core/domain/school/user/AvailableEducationalMember'

/**
 * UseCase pour ajouter des membres existants à une période scolaire
 */
export class AddExistingMembersToSchoolPeriodUseCase {
  private readonly BATCH_SIZE = 5 // Maximum 5 requêtes simultanées

  constructor(private schoolPeriodUserRepository: ISchoolPeriodUserRepository) {}

  /**
   * Associe des membres existants à une période scolaire
   * @param schoolId ID de l'école
   * @param schoolPeriodId ID de la période scolaire
   * @param members Membres à associer
   * @param defaultRole Rôle par défaut à attribuer
   * @returns Les SchoolPeriodUser créés
   */
  async execute(
    schoolId: number,
    schoolPeriodId: number,
    members: AvailableEducationalMember[]
  ): Promise<{
    successful: SchoolPeriodUser[]
    failed: Array<{ member: AvailableEducationalMember; error: string }>
  }> {
    const successful: SchoolPeriodUser[] = []
    const failed: Array<{ member: AvailableEducationalMember; error: string }> = []

    // Traiter les membres par lots
    for (let i = 0; i < members.length; i += this.BATCH_SIZE) {
      const batch = members.slice(i, i + this.BATCH_SIZE)

      // Créer les promesses pour ce lot
      const batchPromises = batch.map((member) =>
        this.addSingleMember(schoolId, schoolPeriodId, member)
      )

      // Attendre que toutes les requêtes du lot se terminent
      const batchResults = await Promise.allSettled(batchPromises)

      // Traiter les résultats
      batchResults.forEach((result, index) => {
        const member = batch[index]

        if (result.status === 'fulfilled') {
          successful.push(result.value)
        } else {
          failed.push({
            member,
            error: result.reason?.message || "Erreur lors de l'ajout du membre"
          })
        }
      })
    }

    return { successful, failed }
  }

  /**
   * Ajoute un membre unique à la période scolaire
   */
  private async addSingleMember(
    schoolId: number,
    schoolPeriodId: number,
    member: AvailableEducationalMember
  ): Promise<SchoolPeriodUser> {
    // Créer l'objet input pour la modification
    const userInput = SchoolPeriodUserInput.from(schoolPeriodId, member)

    // Utiliser la méthode updateSchoolPeriodUser qui fait un PUT
    return await this.schoolPeriodUserRepository.updateSchoolPeriodUser(
      schoolId,
      schoolPeriodId,
      userInput
    )
  }
}
